<template>
	<main>
		<section class="mb-4" :class="$isMobile.any ? 'px-4' : 'mx-auto max-w-6xl'">
			<div class="text-center py-20">
				<h1 class="text-4xl">hokify Karriere-Quiz:</h1>
				<h2 class="text-lg">
					Dein Wegweiser für Berufsorientierung, <br />
					Arbeitsalltag und Bewerbung im Quiz-Format!
				</h2>
			</div>
			<ContentMediaSplitComponent class="shadow-xl" :content="quizCard" ssr />
		</section>
		<section class="pb-24" :class="$isMobile.any ? 'px-4' : 'flex flex-wrap mx-auto max-w-6xl'">
			<div
				v-for="(quiz, index) in shuffledQuizzes"
				:key="quiz.title"
				:class="{
					'w-1/3': !$isMobile.any,
					'pl-2': !$isMobile.any && index % 3 !== 0,
					'pr-2': !$isMobile.any && index % 3 !== 2
				}"
			>
				<div class="shadow-xl rounded-lg bg-white mb-4">
					<div class="relative overflow-hidden rounded-xl rounded-b-none h-64">
						<NuxtImg
							v-if="quiz.image"
							:src="`/${quiz.image}`"
							:alt="quiz.title"
							class="object-cover w-full aspect-4/3"
							ssr
						/>
					</div>
					<div class="flex flex-col text-center p-8 h-[254px]">
						<h2 class="mb-4 h-11 text-base break-word text-text font-bold">
							{{ quiz.title }}
						</h2>
						<p class="h-24">{{ quiz.teaser }}</p>
						<div class="flex justify-center">
							<HokButton color="main" :to="quiz.link"> Quiz starten </HokButton>
						</div>
					</div>
				</div>
			</div>
		</section>
		<section
			class="flex flex-col bg-[var(--color-grey-background)]"
			:class="$isMobile.any ? 'px-8 pt-10 pb-20' : 'pt-20 pb-28 items-center'"
		>
			<h3 class="text-text text-2xl font-bold" :class="$isMobile.any ? 'text-start mb-6' : 'mb-12'">
				Perfekte Vorbereitung
			</h3>
			<p class="max-w-2xl">
				Egal ob du dich für dein Vorstellungsgespräch vorbereiten willst, über einen Jobwechsel
				nachdenkst oder einfach nur wissen möchtest, welche Ausbildung zu dir passt - wir sind für
				dich da! Mache das passende hokify Karriere-Quiz und lass uns dir mit Berufsorientierung,
				Arbeitsalltag und deinen Gehaltsverhandlungen helfen. Kostenloser Berufstest und mehr!
			</p>
		</section>
	</main>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import { getAllAlternateUrls, getCanonicalUrl } from '~/helpers/metadata';
import ContentMediaSplitComponent from '~/components/website/ContentMediaSplitComponent.vue';
import { useRootStore } from '@/stores/root';

export default defineNuxtComponent({
	name: 'KarriereQuiz',
	components: { ContentMediaSplitComponent },
	setup() {
		definePageMeta({
			layout: 'default'
		});

		const rootStore = useRootStore();
		const path = `${rootStore.host}/lp/karriere-quiz`;
		const canonical = getCanonicalUrl(path);
		const alternates = getAllAlternateUrls(path);
		const result = {
			title: `Karriere-Quiz | Vorstellungsgespräch, Berufsorientierung & mehr`,
			link: [canonical, ...alternates],
			meta: [
				{ name: 'robots', content: 'index, follow' },
				{
					name: 'description',
					content:
						'Entdecke das hokify Karriere-Quiz: die perfekte Vorbereitung für Vorstellungsgespräche, Jobwechsel und Gehaltsverhandlungen.'
				}
			]
		};

		useHead(result);
	},
	computed: {
		...mapStores(useRootStore)
	},
	async asyncData({ $pinia }) {
		const rootStore = useRootStore($pinia);

		const quizzes = [
			{
				title: 'Vorstellungsgespräch-Check',
				teaser:
					'Vorstellungsgespräch-Check: Finde heraus, ob du für dein Bewerbungsgespräch vorbereitet bist!',
				link: '/lp/vorstellungsgespraech-check',
				image: 'img/b2c-lps/women-with-laptop.jpg',
				regions: ['at', 'de']
			},
			{
				title: 'Jobwechsel: Ja oder Nein?',
				teaser:
					'Unglücklich im Job? Mache das hokify Quiz und finde heraus, ob du den Job wechseln solltest oder nicht!',
				image: 'img/b2c-lps/women-crossing-arms.jpg',
				link: '/lp/jobwechsel-quiz',
				regions: ['at', 'de']
			},
			{
				title: 'Lehre oder Studium?',
				teaser:
					'Lehre oder doch lieber Studium? Mache das hokify Quiz und finde heraus, ob die Lehre oder das Studium zu dir passt!',
				image: 'img/b2c-lps/women-with-phone-sitting.jpg',
				link: '/lp/lehre-oder-studium-quiz',
				regions: ['at']
			},
			{
				title: 'Quiz: 4 Tage Woche',
				teaser:
					'Bessere Work-Life-Balance mit 4 Tage Woche? Mache das hokify Quiz und finde heraus, ob die 4tage Woche zu dir passt!',
				image: 'img/b2c-lps/women-with-phone.jpg',
				link: '/lp/vier-tage-woche-quiz',
				regions: ['at', 'de']
			},
			{
				title: 'Ausbildung oder Abitur?',
				teaser:
					'Ausbildung oder doch Abitur? Mache das hokify Quiz und finde heraus, ob die Ausbildung oder das Abi das Richtige für dich ist!',
				image: 'img/b2c-lps/jobchange/jobchange-women.png',
				link: '/lp/ausbildung-oder-abitur-quiz',
				regions: ['de']
			},
			{
				title: 'Karriere als Kellner:in?',
				teaser:
					'Was macht ein Kellner? Mache das hokify Quiz und finde heraus, ob du Kellnerin werden solltest!',
				image: 'img/b2c-lps/women-paying.jpg',
				link: '/lp/kellner-quiz',
				regions: ['at', 'de']
			},
			{
				title: 'Welcher Minijob passt zu dir?',
				teaser:
					'Geringfügige Beschäftigung gesucht? Mache das hokify Quiz und finde heraus, welcher Minijob zu deinen Stärken passt!',
				image: 'img/b2c-lps/women-with-phone-sitting.jpg',
				link: '/lp/minijob-quiz',
				regions: ['de']
			},
			{
				title: 'Welcher Nebenjob passt zu dir?',
				teaser:
					'Geringfügige Beschäftigung gesucht? Mache das hokify Quiz und finde heraus, welcher Nebenjob zu deinen Stärken passt!',
				image: 'img/b2c-lps/jobchange/jobchange-women.png',
				link: '/lp/nebenjob-quiz',
				regions: ['at']
			},
			{
				title: 'Welcher Home Office Typ bist du?',
				teaser:
					'Bist du produktiver im Home Office? Mache das hokify Quiz und finde heraus, welcher Home-Office-Typ du bist!',
				image: 'img/b2c-lps/women-on-couch.jpg',
				link: '/lp/home-office-typ-quiz',
				regions: ['at', 'de']
			},
			{
				title: 'Welche Lehre passt zu mir?',
				teaser:
					'Handwerkliche oder kaufmännische Lehre? Mache das hokify Quiz und finde heraus, welche Lehre am besten zu dir passt!',
				image: 'img/b2c-lps/women-train.jpg',
				link: '/lp/lehre-quiz',
				regions: ['at']
			},
			{
				title: 'Welche Ausbildung passt zu mir?',
				teaser:
					'Pflegeausbildung oder handwerkliche Ausbildung? Mache das hokify Quiz und finde heraus, welche Ausbildung am besten zu dir passt!',
				image: 'img/b2c-lps/women-train.jpg',
				link: '/lp/ausbildungs-quiz',
				regions: ['de']
			},
			{
				title: 'Härteste Jobs der Welt - Welcher passt zu dir?',
				teaser:
					'Hast du dich schon immer gefragt, was der härteste Job der Welt ist? Mache das hokify-Quiz und finde heraus, welchen schweren Job du meistern würdest!',
				image: 'img/b2c-lps/women-cutting.jpg',
				link: '/lp/haerteste-jobs-quiz',
				regions: ['at', 'de']
			},
			{
				title: 'Gehaltserhöhung verhandeln!',
				teaser:
					'Gehaltserhöhung verhandeln: Mit diesem hokify-Quiz bist du Top vorbereitet für deine nächste Gehaltsverhandlung, inklusive Tipps & Argumente für die Gehaltsverhandlung! ',
				image: 'img/b2c-lps/gnc/gnc-jobinterviewtraining.png',
				link: '/lp/gehltsverhandlung-quiz',
				regions: ['at', 'de']
			}
		];

		const newestQuiz:
			| {
					title: string;
					teaser: string;
					image: string;
					link: string;
					regions: string[];
			  }
			| undefined = quizzes.findLast(({ regions }) => regions.includes(rootStore.topLevelDomain));

		const shuffledQuizzes = quizzes.filter(
			quiz => quiz.title !== newestQuiz?.title && quiz.regions.includes(rootStore.topLevelDomain)
		);

		for (let i = shuffledQuizzes.length - 1; i > 0; i -= 1) {
			const j = Math.floor(Math.random() * (i + 1));
			const temp = shuffledQuizzes[i];
			shuffledQuizzes[i] = shuffledQuizzes[j];
			shuffledQuizzes[j] = temp;
		}

		const quizCard = {
			type: 'img',
			mediaPosition: 'right',
			src: newestQuiz?.image,
			title: newestQuiz?.title,
			aspectRatio: 'aspect-square',
			text: newestQuiz?.teaser,
			buttonText: 'Quiz starten',
			buttonLink: newestQuiz?.link
		};

		return {
			shuffledQuizzes,
			quizCard
		};
	},
	data() {
		const shuffledQuizzes:
			| {
					title: string;
					teaser: string;
					image: string;
					link: string;
					regions: string[];
			  }[]
			| undefined = undefined;
		const quizCard:
			| {
					type: 'img' | 'video';
					mediaPosition: 'left' | 'right';
					src: string;
					title: string;
					alt?: string;
					poster?: string;
					aspectRatio?: string;
					text: string;

					buttonText: string;
					buttonLink: string;
			  }
			| undefined = undefined;

		return {
			quizCard,
			shuffledQuizzes
		};
	}
});
</script>
